import {
  logAniOsChunkReload,
  shouldAutoReloadPageForChunkLoadError,
} from "@/utils/aniosDiagnostics";

/** sessionStorage key: prevents infinite reload loops when HTML/assets stay stale */
export const CHUNK_RELOAD_SESSION_KEY = "ani-chunk-reload-attempted";

export interface ChunkReloadOptions {
  source?: string;
  meta?: Record<string, unknown>;
}

const CHUNK_LOAD_MESSAGE_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "error loading dynamically imported module",
  "Failed to load module script",
] as const;

const CHUNK_LOAD_REGEX = /Loading chunk [\d]+ failed/i;

function collectErrorMessages(error: unknown): string[] {
  const messages: string[] = [];
  if (error == null) return messages;

  if (typeof error === "string") {
    messages.push(error);
    return messages;
  }

  if (error instanceof Error) {
    messages.push(error.message);
    if (error.cause != null) {
      messages.push(...collectErrorMessages(error.cause));
    }
  }

  if (typeof error === "object" && "message" in error) {
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === "string" && msg) messages.push(msg);
  }

  return messages;
}

/**
 * True when the browser failed to load a code-split chunk (usually stale tab after deploy).
 */
export function isChunkLoadError(error: unknown): boolean {
  const combined = collectErrorMessages(error).join(" ");
  if (!combined) return false;

  if (CHUNK_LOAD_REGEX.test(combined)) return true;

  return CHUNK_LOAD_MESSAGE_PATTERNS.some((fragment) =>
    combined.includes(fragment),
  );
}

/**
 * Clears the one-shot reload guard after a successful app boot so a future deploy can auto-reload again.
 */
export function clearChunkReloadAttemptFlag(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
  } catch {
    // ignore (private mode, blocked storage)
  }
}

/**
 * Reloads the page once per stale-chunk incident. Returns true if a reload was triggered.
 */
export function reloadOnceForStaleChunks(options?: ChunkReloadOptions): boolean {
  if (typeof window === "undefined") return false;

  if (!shouldAutoReloadPageForChunkLoadError()) {
    logAniOsChunkReload(options?.source ?? "app-reload", {
      skipped: true,
      reason: "anios-shell-active",
      ...options?.meta,
    });
    return false;
  }

  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === "1") {
      logAniOsChunkReload(options?.source ?? "app-reload", {
        skipped: true,
        reason: "reload-already-attempted",
        ...options?.meta,
      });
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, "1");
  } catch {
    // If storage is unavailable, still attempt a single reload this navigation.
  }

  logAniOsChunkReload(options?.source ?? "app-reload", options?.meta);
  window.location.reload();
  return true;
}
